import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { IProps, StepsPopup } from '../components/Common/StepsPopup/StepsPopup';
import { getUsefulLinks } from '../actions/adminActions';
import { UsefulLinksGrid } from '../components/Home/UsefulLinks/UsefulLinksGrid';
import { useOverlayContext } from '../components/NewDesignCommon/Overlay';
import { IUsefulLink } from '../entities/IAdmin';
import { ELcid } from '../entities/ILanguage';
import { isGiftContext, isInvitationContext } from './pathContext';
import { withoutUsefulLinksPopupQuery } from './usefulLinksQuery';
import IntlMessage from '../components/Common/IntlMessage';

interface INameByLcid {
    [key: string]: {
        [key in ELcid]?: string;
    };
}

interface IHomeLinksState {
    links: IUsefulLink[];
    nameByLcid: INameByLcid;
    showAllLinks: boolean;
    page: number;
    count: number;
}

export const useUsefulLinksPopup = () => {
    const history = useNavigate();
    const { search } = useLocation();
    const { showOverlay, hideOverlay } = useOverlayContext();

    const closePopup = useCallback(() => {
        history({
            search: withoutUsefulLinksPopupQuery(search)
        });
        hideOverlay();
    }, [history, hideOverlay, search]);

    return useCallback(() => {
        showOverlay<IProps>({
            component: StepsPopup,
            componentProps: {
                showPopup: true,
                title: (
                    <IntlMessage
                        id="individualhome.usefulLinks"
                    />
                ),
                steps: [
                    {
                        content: <UsefulLinksGrid />
                    }
                ],
                finishHandler: closePopup
            }
        });
    }, [closePopup, showOverlay]);
};

export const useUsefulLinksState = (): [IHomeLinksState, () => void] => {
    const dispatch = useDispatch();
    const [state, setState] = useState<IHomeLinksState>({
        links: [],
        nameByLcid: undefined,
        showAllLinks: false,
        page: 0,
        count: 0
    });

    const setNextPage = useCallback(() => {
        setState((currentState) => ({
            ...currentState,
            page: currentState.page + 1
        }));
    }, []);

    useEffect(() => {
        dispatch(getUsefulLinks(state.page, 10)).then((response) => {
            if ((response.items || []).length > 0) {
                setState((currentState) => ({
                    ...currentState,
                    count: response.count,
                    links: [...currentState.links, ...response.items],
                    nameByLcid: {
                        ...(currentState.nameByLcid || {}),
                        ...response.items.reduce(
                            (allLinks, link) => ({
                                ...allLinks,
                                [link.id]: {
                                    ...link.name.reduce(
                                        (names, name) => ({
                                            ...names,
                                            [name.lcid]: name.label
                                        }),
                                        {}
                                    )
                                }
                            }),
                            {}
                        )
                    }
                }));
            }
        });
    }, [state.page]);

    return [state, setNextPage];
};

export const useUsefulLinksRedirect = () =>
    useCallback((e: React.MouseEvent, path: string) => {
        if (
            isGiftContext(path) ||
            isInvitationContext(path)
        ) {
            e.preventDefault();
            window.location.replace(path);
        }
    }, []);
